// src/Pages/Dashboard/Transactions.js
import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import SortableHeaderCell from '../../Components/SortableHeaderCell';
import FilterInput from '../../Components/FilterInput';
import { useTransactionsList } from '../../Hooks';
// import { ListLoading } from '../../Components/Loading';
import Alert from '../../Components/Alert';
import { sessionManager } from '../../Config/cookies';
import { formatDate } from '../../Utils/dateFormatter';
import { getFullName } from '../../Utils/dataFormatter';
import { getDirectionColor, translateDirection } from '../../Utils/transactions';
import { transactionStatuses } from "../../Data/transactionFilters";
import Pagination from '../../Components/Pagination';


const Transactions = () => {
  const currentUserId = sessionManager.restoreSession()?.userData?.id || null;
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(() => Math.max(1, parseInt(searchParams.get('page') || '1', 10)));
  const [ordering, setOrdering] = useState('-created_at');
  const [appliedSearch, setAppliedSearch] = useState('');

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams(prev => { prev.set('page', String(newPage)); return prev; }, { replace: true });
  };

  const [dateFilter, setDateFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const handleSort = (field, direction = 'asc') => {
    setOrdering(direction === 'asc' ? field : `-${field}`);
    setPage(1);
  };

  const handleApplyFilters = () => {
    setAppliedSearch(search.trim());
    handlePageChange(1);
  };

  const handleDateFilterChange = (type, value) => {
    if (type === 'exact') {
      setDateFilter(value);
      setDateFrom('');
      setDateTo('');
    } else if (type === 'from') {
      setDateFrom(value);
      setDateFilter('');
    } else if (type === 'to') {
      setDateTo(value);
      setDateFilter('');
    }
    setPage(1);
  };

  const clearDateFilters = () => {
    setDateFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleKeyDownApply = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  // معاملات الاستعلام للـ API
  const queryParams = useMemo(() => ({
    page,
    search: appliedSearch || undefined,
    direction: (selectedStatusFilter === 'incoming' || selectedStatusFilter === 'outgoing')
      ? selectedStatusFilter
      : undefined,
    is_debt:
      selectedStatusFilter === 'debt'
        ? true
        : selectedStatusFilter === 'paid'
          ? false
          : undefined,
    created_at: dateFilter || undefined,
    created_from: dateFrom || undefined,
    created_to: dateTo || undefined,
    ordering,
  }), [page, appliedSearch, selectedStatusFilter, dateFilter, dateFrom, dateTo, ordering]);

  // جلب من الـ API
  const { transactions, total, isLoading, error, refetch } = useTransactionsList(queryParams);


  // نستعمل النتائج كما تأتي من الـ API
  const transactionsFiltered = transactions;



  const computeDirection = (t) => {
    if (!currentUserId) return null;
    if (t?.to_user?.id === currentUserId) return 'incoming';
    if (t?.from_user?.id === currentUserId) return 'outgoing';
    return null;
  };

  if (error) {
    return (
      <div className="p-6 bg-light min-h-screen font-cairo dir-rtl">
        <Alert type="error" message={error} onClose={() => refetch()} />
      </div>
    );
  }

  if (isLoading && transactions.length === 0) {
    return (
      <div className="p-2 md:p-4 bg-light min-h-screen direction-rtl flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-main1 text-2xl mx-auto mb-4" />
          <p className="text-main1 font-cairo">جاري تحميل البيانات ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-light min-h-screen font-cairo dir-rtl">
      {/* العنوان وعدد المعاملات */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-main1">معاملات المحفظة</h2>
          <span className="text-gray-500 text-sm">
            إجمالي المعاملات <b className="text-main2">{total}</b>
          </span>
        </div>

        {/* حقل البحث */}
        <div className="w-full md:w-80">
          <div className="relative">
            <FilterInput
              type="text"
              placeholder="بحث في المعاملات..."
              value={search}
              onChange={e => {
                const val = e.target.value;
                setSearch(val);
                if (val.trim() === '' && appliedSearch !== '') {
                  setAppliedSearch('');
                  setPage(1);
                }
              }}
              onKeyDown={handleKeyDownApply}
              className="w-full pl-10 pr-4"
            />
            <button
              onClick={handleApplyFilters}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-main1 transition-transform hover:scale-110 active:scale-95 focus:outline-none rounded-full"
              title="بحث"
            >
              <FaSearch />
            </button>
          </div>
        </div>
      </div>

      {/* الفلاتر */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div className="flex flex-wrap gap-2">
          {transactionStatuses.map(f => (
            <button
              key={f.value}
              onClick={() => { setSelectedStatusFilter(f.value); handlePageChange(1); }}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150
                ${selectedStatusFilter === f.value ? 'bg-main1 text-light border-main1' : 'bg-grayish text-main1 border-gray-200 hover:bg-main1/10'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* فلتر التاريخ */}
        <div className="flex items-center gap-2">
          <FilterInput
            label="تاريخ محدد"
            type="date"
            value={dateFilter}
            onChange={(e) => handleDateFilterChange('exact', e.target.value)}
            placeholder="YYYY-MM-DD"
          />
          <FilterInput
            label="من تاريخ"
            type="date"
            value={dateFrom}
            onChange={(e) => handleDateFilterChange('from', e.target.value)}
            placeholder="YYYY-MM-DD"
          />
          <FilterInput
            label="إلى تاريخ"
            type="date"
            value={dateTo}
            onChange={(e) => handleDateFilterChange('to', e.target.value)}
            placeholder="YYYY-MM-DD"
          />
          {(dateFilter || dateFrom || dateTo) && (
            <button
              onClick={clearDateFilters}
              className="text-xs rounded-xl text-main1 bg-grayish hover:bg-opacity-90 transition px-2 py-2"
            >
              مسح الفلترة
            </button>
          )}
        </div>
      </div>

      {/* الجدول */}
      <div className="overflow-x-auto rounded-2xl shadow-md bg-transparent dir-rtl">
        <table className="min-w-full text-sm text-main1 font-cairo">
          <thead>
            <tr className="bg-grayish text-main1">
              <th className="p-4 text-center">
                <SortableHeaderCell
                  label="التاريخ"
                  field="created_at"
                  ordering={ordering}
                  onSort={handleSort}
                  align="center"
                  ascTitle="الأقدم أولاً"
                  descTitle="الأحدث أولاً"
                />
              </th>
              <th className="p-4 text-center">اتجاه المعاملة</th>
              <th className="p-4 text-right">من</th>
              <th className="p-4 text-right">إلى</th>
              <th className="p-4 text-center">
                <SortableHeaderCell
                  label="المبلغ الكلي"
                  field="amount_sp"
                  ordering={ordering}
                  onSort={handleSort}
                  align="center"
                  ascTitle="الأقل أولاً"
                  descTitle="الأكثر أولاً"
                />
              </th>
              <th className="p-4 text-center">المدفوع</th>
              <th className="p-4 text-center">دين</th>
              <th className="p-4 text-right">ملاحظة</th>
            </tr>
          </thead>
          <tbody>
            {transactionsFiltered.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-16">
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-gray-500">لا توجد معاملات</span>
                  </div>
                </td>
              </tr>
            ) : (
              transactionsFiltered.map((t) => {
                const direction = computeDirection(t);
                return (
                  <tr key={t.id} className="border-b last:border-b-0 hover:bg-grayish/60 transition">
                    <td className="p-4 text-center text-xs whitespace-pre-line">{formatDate(t.created_at)}</td>
                    <td className="p-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getDirectionColor(direction)}`}>
                        {translateDirection(direction)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="text-right">
                        <div className="font-medium text-main1">{getFullName(t.from_user)}</div>
                        <div className="text-xs text-gray-500">{t.from_user?.phone_number || '-'}</div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-right">
                        <div className="font-medium text-main1">{getFullName(t.to_user)}</div>
                        <div className="text-xs text-gray-500">{t.to_user?.phone_number || '-'}</div>
                      </div>
                    </td>
                    <td className="p-4 text-center font-medium">{t.amount_sp ?? '-'}</td>
                    <td className="p-4 text-center font-medium">{t.amount_paid ?? '-'}</td>
                    <td className="p-4 text-center font-medium">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${t.is_debt ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                        {t.is_debt ? 'نعم' : 'لا'}
                      </span>
                    </td>
                    <td className="p-4 text-right">{t.note || '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>


      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={Math.ceil(total / 20)}
        onPageChange={handlePageChange}
        totalItems={total}
        itemsPerPage={20}
        loading={isLoading}
      />
    </div>
  );
};

export default Transactions;

